import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Lock, Eye, EyeOff, CheckCircle2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

export default function ChangePasswordPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Optional: keep user state fresh — refetch /auth/me on mount
  useEffect(() => {
    if (!user) return;
    api.get('/auth/me').catch(() => {});
  }, [user]);

  const mustChange = !!user?.must_change_password;

  const validate = () => {
    if (!form.current) return 'Please enter your current password';
    if (form.next.length < 8) return 'New password must be at least 8 characters';
    if (form.next === form.current) return 'New password cannot be the same as the current password';
    if (form.next !== form.confirm) return 'Passwords do not match';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/change-password', {
        current_password: form.current,
        new_password: form.next,
      });
      setUser(data);
      setDone(true);
      toast.success('Password updated successfully');
      setTimeout(() => navigate('/admin', { replace: true }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-change-password-page" className="max-w-xl">
        <h1 className="font-display text-3xl text-white inline-flex items-center gap-2">
          <Lock size={22} className="text-[hsl(var(--sb-gold))]" /> CHANGE PASSWORD
        </h1>
        <p className="text-sm text-white/65 mt-1">
          {mustChange
            ? 'For security, please change your default password before continuing.'
            : 'Update your account password.'}
        </p>

        {mustChange && (
          <div className="mt-4 p-4 rounded-md border border-[hsl(var(--sb-gold))]/40 bg-[hsl(var(--sb-gold))]/10 flex gap-3 items-start" data-testid="must-change-banner">
            <ShieldAlert className="text-[hsl(var(--sb-gold))] mt-0.5" size={18} />
            <div>
              <div className="font-display text-base text-[hsl(var(--sb-gold))]">FIRST-TIME LOGIN</div>
              <p className="text-sm text-white/85 mt-0.5">You're using a temporary password. Set a new one to unlock the dashboard.</p>
            </div>
          </div>
        )}

        {done ? (
          <div className="mt-6 p-5 rounded-md border border-[hsl(var(--sb-green))]/30 bg-[hsl(var(--sb-green))]/10">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-green))]">
              <CheckCircle2 size={20} /> <span className="font-display text-xl">PASSWORD UPDATED</span>
            </div>
            <p className="text-sm text-white/80 mt-2">Redirecting you to the dashboard…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4 sb-card p-5" data-testid="change-password-form">
            {[
              { key: 'current', label: 'Current password', testid: 'change-password-current' },
              { key: 'next', label: 'New password (min 8 characters)', testid: 'change-password-new' },
              { key: 'confirm', label: 'Confirm new password', testid: 'change-password-confirm' },
            ].map((f) => (
              <label key={f.key} className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">{f.label}</span>
                <div className="mt-1 relative">
                  <input
                    data-testid={f.testid}
                    type={show[f.key] ? 'text' : 'password'}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    required
                    minLength={f.key === 'current' ? 1 : 8}
                    className="w-full h-11 pl-3 pr-10 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]"
                  />
                  <button
                    type="button"
                    onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 h-7 w-7 grid place-items-center"
                    aria-label="Toggle visibility"
                  >
                    {show[f.key] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {f.key === 'confirm' && form.confirm && form.confirm !== form.next && (
                  <span className="text-[11px] text-[hsl(var(--sb-red))] mt-1 block">Passwords do not match.</span>
                )}
              </label>
            ))}
            <div className="flex gap-3 pt-1">
              <button
                data-testid="change-password-submit"
                type="submit"
                disabled={submitting}
                className="flex-1 h-12 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] disabled:opacity-50"
              >
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </div>
            <div className="text-[11px] text-white/45">
              Last changed: {user?.password_changed_at ? new Date(user.password_changed_at).toLocaleString('en-GB') : 'never'}
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}
